import { v4 as uuidv4 } from 'uuid';

import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { IFederalDistrict } from 'APIPackage/KnmModule/interfaces/IFederalDistrict';
import { IKnmObject248 } from 'APIPackage/KnmModule/interfaces/IKnmObject248';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { getDistrictSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { getDictionarySelectOptionWithState } from 'CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { IKnmErknmFormObject } from '../interfaces/IKnmErknmFormObject';

/**
 * Конвертер серверного объекта контроля в значение формы (IKnmErknmFormObject).
 */
export const convertObjectDataToFormValue = (
  object: IKnmObject248,
  state: IRootState,
  currentObject?: IKnmErknmFormObject | undefined,
): IKnmErknmFormObject => {
  const dangerClassSelectOption = object.dangerClass?.dictId
    ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.EVENT_TYPE>(
        state,
        object.dangerClass?.dictId,
        object.dangerClass?.dictVersionId,
      )
    : undefined;
  const objectKindSelectOption = object.objectKind?.dictId
    ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_KIND>(
        state,
        object.objectKind?.dictId,
        object.objectKind?.dictVersionId,
      )
    : undefined;
  const objectSubKindSelectOption = object.objectSubKind?.dictId
    ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_SUB_KIND>(
        state,
        object.objectSubKind?.dictId,
        object.objectSubKind?.dictVersionId,
      )
    : undefined;
  const objectTypeSelectOption = object.objectType?.dictId
    ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.SUBJECT_TYPE>(
        state,
        object.objectType?.dictId,
        object.objectType?.dictVersionId,
      )
    : undefined;
  const riskCategorySelectOption = object.riskCategory?.dictId
    ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_CATEGORY>(
        state,
        object.riskCategory?.dictId,
        object.riskCategory?.dictVersionId,
      )
    : undefined;

  let objRegion: IKnmDistrictSelectOption | undefined;
  let objFiasRegion: IKnmDistrictSelectOption | undefined;

  const list = getDistrictSelectOptions(state);

  if (object?.region) {
    if (typeof object.region === 'object') {
      objRegion = list.find(i => i.id.toString() === (object.region as IFederalDistrict)?.id?.toString());
    } else {
      objRegion = list.find(i => i.id.toString() === object?.region);
    }
  }
  if (object?.fiasRegion) {
    if (typeof object.fiasRegion === 'object') {
      objFiasRegion = list.find(i => i.id?.toString() === (object.fiasRegion as IFederalDistrict)?.id?.toString());
    } else {
      objFiasRegion = list.find(i => i.id.toString() === object?.fiasRegion);
    }
  }

  const inspectionResults = object.inspectionResults || [];
  const parentInspectionResultKind = inspectionResults.find(
    i => i.inspectedField === InspectionResultFieldEnum.OBJECT_KIND,
  );
  const parentInspectionResultSubKind = inspectionResults.find(
    i => i.inspectedField === InspectionResultFieldEnum.OBJECT_SUB_KIND,
  );
  const parentInspectionResultType = inspectionResults.find(
    i => i.inspectedField === InspectionResultFieldEnum.OBJECT_TYPE,
  );
  const parentInspectionResultDanger = inspectionResults.find(
    i => i.inspectedField === InspectionResultFieldEnum.DANGER_CLASS,
  );
  const parentInspectionResultRisk = inspectionResults.find(
    i => i.inspectedField === InspectionResultFieldEnum.RISK_CATEGORY,
  );

  return {
    address: object.address || undefined,
    objectType:
      (objectTypeSelectOption && {
        ...objectTypeSelectOption,
        inspectionResult: object.objectType?.inspectionResult || parentInspectionResultType,
      }) ||
      currentObject?.objectType,
    objectKind:
      (objectKindSelectOption && {
        ...objectKindSelectOption,
        inspectionResult: object.objectKind?.inspectionResult || parentInspectionResultKind,
      }) ||
      currentObject?.objectKind,
    objectSubKind:
      (objectSubKindSelectOption && {
        ...objectSubKindSelectOption,
        inspectionResult: object.objectSubKind?.inspectionResult || parentInspectionResultSubKind,
      }) ||
      currentObject?.objectSubKind,
    riskCategory:
      (riskCategorySelectOption && {
        ...riskCategorySelectOption,
        inspectionResult: object.riskCategory?.inspectionResult || parentInspectionResultRisk,
      }) ||
      currentObject?.riskCategory,
    dangerClass:
      (dangerClassSelectOption && {
        ...dangerClassSelectOption,
        inspectionResult: object.dangerClass?.inspectionResult || parentInspectionResultDanger,
      }) ||
      currentObject?.dangerClass,
    guid: object.guid || undefined,
    ervkNumber: object.ervkNumber || undefined,
    ervkId: object.ervkId || undefined,
    tmpErvkCosId: object.tmpErvkCosId || undefined,
    cosId: object.cosId || undefined,
    cosVersion: object.cosVersion || undefined,
    name: object.name || undefined,
    streetGuid: object.streetGuid || undefined,
    houseGuid: object.houseGuid || undefined,
    roomGuid: object.roomGuid || undefined,
    steadGuid: object.steadGuid || undefined,
    addressFns: object.addressFns || undefined,
    autocomplete: typeof object.autocomplete === 'boolean' ? object.autocomplete : currentObject?.autocomplete,
    dateCreate: object.dateCreate || undefined,
    geolocation: object.geolocation || undefined,
    listId: currentObject?.listId || object.guid || uuidv4(),
    isNew: currentObject?.isNew ?? !!object.isNew,
    manualAddress: object.manualAddress || undefined,
    notInFIAS: object.notInFIAS || undefined,
    region: objRegion || currentObject?.region,
    fiasRegion: objFiasRegion || currentObject?.fiasRegion,
    regionName: object.regionName || undefined,
    isErrorFiasValue: currentObject?.isErrorFiasValue,
    inspectionResults: object.inspectionResults || undefined,
    organizationErvkId: object.organizationErvkId || undefined,
    updating: object.updating || undefined,
    isFilledFromLink: object.isFilledFromLink || currentObject?.isFilledFromLink,
  };
};
